import type { ApprovalMode } from '../../api/agents-client/agent-client.type';
import { GeneralViewNames } from '../side-bar/side-bar.type';

type AskUserQuestionOption = {
  label: string;
  description?: string;
};

type AskUserQuestion = {
  question: string;
  header?: string;
  options: AskUserQuestionOption[];
  multi_select: boolean;
};

type ArtifactType =
  | 'flow_by_phase_chart'
  | 'freeform_chart'
  | 'measure_comparison_chart'
  | 'measures_over_time_chart'
  | 'process_map_chart'
  | 'work_pace_chart'
  // A published Flora page previewed in the chat rail. Handled by the dedicated
  // page panel (its `data` carries only `page_id`), NOT the chart-card registry.
  | 'page';

type ArtifactSpec = {
  id: string;
  type: ArtifactType;
  title: string;
  data: Record<string, unknown>;
  config?: Record<string, unknown>;
};

enum FloraVersion {
  LANGCHAIN = 'flora-langchain',
}

type ChatHistory = {
  id: string;
  last_accessed: string;
  description: string;
  messages: Message[];
  token_usage: TokenUsage | null;
  flora_version: string | null;
  suggested_followups: string[];
  // Ask/Auto mode this chat was switched to from the composer; null follows
  // the user's saved default. Absent on older backends.
  approval_mode?: ApprovalMode | null;
  // Latest resumable job (parked on approval, or running with a fresh
  // heartbeat) whose turn is not in `messages` yet — reconnect to replay it.
  // Orphaned runs are excluded server-side; the client must not re-filter.
  active_job_id?: string | null;
};

type TokenUsage = {
  context_used: number;
  context_limit: number;
  percentage: number;
};

/**
 * Lifecycle of a tool call as seen by the UI.
 * - `streaming`: the model is still writing the call's arguments (partial JSON
 *   fragments arrive over the socket before the tool runs).
 * - `drafted`: the model finished writing the arguments but the tool has not
 *   started (e.g. it is waiting for human approval, or was rejected). Also the
 *   final state of a call auto mode blocked (`auto_mode` is `blocked` or
 *   `unavailable`), which never runs.
 * - `pending`: the tool is executing.
 * - `completed` / `error`: terminal states.
 */
type ToolCallStatus = 'streaming' | 'drafted' | 'pending' | 'completed' | 'error';

/**
 * The auto-mode risk check's decision on an approval-required tool call.
 * - `Approved`: judged safe and run without asking the user.
 * - `Blocked`: judged risky and not run.
 * - `Unavailable`: the risk check could not run, so the call was not run.
 * - `Invalid`: not an auto-mode judgement — pre-validation found the call could
 *   not succeed (e.g. a missing Jira ticket), in any approval mode, so it was not run.
 */
enum AutoModeOutcome {
  Approved = 'approved',
  Blocked = 'blocked',
  Unavailable = 'unavailable',
  Invalid = 'invalid',
}

/**
 * Why a chat session was reset, passed to the host's `onNewChat` so it can
 * decide whether to navigate.
 *
 * - `User`: an explicit New Chat, or deleting the open chat.
 * - `ContextSwitch`: the organization or portfolio changed underneath the
 *   session. Hosts on a non-chat page (e.g. settings) should stay put.
 */
enum FloraNewChatReason {
  User = 'user',
  ContextSwitch = 'context_switch',
}

type ToolCall = {
  id: string;
  tool_name: string;
  tool_input: Record<string, unknown>;
  tool_output?: string;
  tool_error?: string;
  status: ToolCallStatus;
  parent_context?: string | null; // Subagent name if this tool call is from a subagent
  /** Provider tool-call id, present when the call was observed while streaming. */
  tool_call_id?: string | null;
  /** Raw JSON fragments of the arguments accumulated so far while `streaming`. */
  partial_input?: string;
  /** Auto mode's decision, present only when auto mode approved or blocked the call. */
  auto_mode?: AutoModeOutcome;
};

// Subagent lifecycle tracking
type SubagentInfo = {
  // Per-instance id (e.g. "general-purpose:run-789") for the LangChain agent,
  // which can run several same-named subagents in parallel. Older persisted
  // chats omit it, so grouping falls back to `name`.
  id?: string;
  name: string;
  description?: string;
  status: 'in_progress' | 'completed' | 'error';
  output?: string;
  error?: string;
};

type MessageSegment =
  | { type: 'text'; content: string; parent_context?: string | null }
  | { type: 'tool_call'; toolCall: ToolCall }
  | { type: 'subagent_start'; subagent: SubagentInfo }
  | { type: 'subagent_end'; subagent: SubagentInfo }
  | { type: 'artifact'; artifact: ArtifactSpec }
  | {
      type: 'context_compacted';
      percentage: number;
      finished?: boolean;
    }
  | { type: 'ask_user_questions'; questions: AskUserQuestion[] }
  | { type: 'user_answers'; answers: Record<string, string | string[]> }
  | { type: 'tool_approval'; toolApproval: ToolApproval; decision?: ToolApprovalResolution }
  | { type: 'interrupted' }
  | { type: 'error'; message: string };

// Content block format from backend (new format)
type ContentBlock =
  | { type: 'text'; text: string; parent_context?: string | null }
  | {
      type: 'tool_call';
      name: string;
      input: Record<string, unknown>;
      // Absent when auto mode blocked the call (it never ran).
      output?: string;
      parent_context?: string | null;
      tool_call_id?: string;
      auto_mode?: AutoModeOutcome;
      // Why a pre-validation-blocked (`invalid`) call could not run.
      error?: string;
      is_subagent?: boolean;
      subagent_name?: string;
      // Per-instance id for LangChain parallel subagents; children carry it as
      // their `parent_context`. Absent on older chats (which key on the name).
      subagent_id?: string;
    }
  | { type: 'subagent_start'; subagent_name: string; description?: string }
  | { type: 'subagent_end'; subagent_name: string; output?: string; error?: string }
  | { type: 'artifact'; artifact: ArtifactSpec }
  | {
      type: 'context_compacted';
      context_used: number;
      context_limit: number;
      percentage: number;
    }
  | { type: 'ask_user_questions'; data: { questions: AskUserQuestion[] } }
  | { type: 'user_answers'; answers: Record<string, string | string[]> }
  | { type: 'interrupted' };

/**
 * Document attached to a message (from Flora document tools)
 */
type Document = {
  filename: string;
  doc_type: 'pptx' | 'xlsx' | 'docx' | 'pdf' | 'png' | 'jpg' | 'csv' | 'tsv' | 'txt' | 'md' | 'json';
  download_url: string;
  document_id: string;
  file_size?: number;
  /** Bumped each time Flora edits the document; used to refetch a stale preview. */
  revision?: number;
};

/**
 * A single action from the HITL middleware requesting human review.
 */
type ActionRequest = {
  name: string;
  args: Record<string, unknown>;
  description?: string;
};

/**
 * Review configuration for a single action.
 */
type ReviewConfig = {
  action_name: string;
  allowed_decisions: string[];
};

/**
 * Pending tool approval from the HITL middleware.
 * Shown to the user as an approval card before Jira write actions execute.
 */
type ToolApproval = {
  actionRequests: ActionRequest[];
  reviewConfigs: ReviewConfig[];
  threadId: string;
  jobId: string;
};

/**
 * The user's decision on a pending tool approval, as sent to the backend.
 * 'approve_always' approves and stores an auto-approve preference so Flora
 * stops asking for this tool (mirrors the Customize Flora toggle).
 */
type ToolApprovalDecision = 'approve' | 'approve_always' | 'reject';

/**
 * How a tool_approval segment was resolved, for rendering.
 */
type ToolApprovalResolution = 'approved' | 'approved_always' | 'rejected';

/**
 * A user-uploaded Word/Excel file attached to a user message. Distinct from
 * `documents` (which are Flora-produced); rendered as a lightweight chip.
 */
type MessageAttachment = {
  document_id: string;
  filename: string;
  /**
   * Reuses `Document['doc_type']` rather than a bare `string` so an unhandled
   * type is a compile error at the `DOC_TYPE_CONFIG` lookups instead of a
   * silent fallback icon. Uploadable types are a subset of this union.
   */
  doc_type: Document['doc_type'];
};

type Message = {
  content: string | ContentBlock[];
  role: string;
  timestamp: string;
  /**
   * Stable identity pairing a user question with its assistant reply.
   * Assigned client-side — for historical messages by
   * `deriveTurnsFromHistory` (flora-turn-pairing.helpers.ts), for live
   * turns by `createTurnId` (flora-hooks.ts) — never sent to or read from
   * the backend. Optional so pre-existing Message literals and fixtures
   * remain valid; consumers must fall back to positional pairing when
   * absent.
   */
  turnId?: string;
  images?: string[];
  documents?: Document[];
  attachments?: MessageAttachment[];
  artifacts?: ArtifactSpec[];
  toolCalls?: ToolCall[];
  segments?: MessageSegment[];
  activeSubagents?: SubagentInfo[]; // Track currently active subagents for nesting
  suggestedFollowups?: string[];
  pendingQuestions?: AskUserQuestion[];
  pendingJobId?: string;
  pendingToolApproval?: ToolApproval;
  /**
   * Why the backend refused the last answer or approval sent for this
   * message's pending prompt. Shown on the re-armed card; cleared by the next
   * submit or a new prompt.
   */
  promptRejection?: string;
  isLoading?: boolean;
};

enum FloraSupportedObjectTypes {
  Portfolio = 'portfolio',
  Project = 'project',
  Subproject = 'subproject',
  Initiative = 'initiative',
  WorkPeriod = 'work_period',
  Dashboard = 'dashboard',
  Objective = 'objective',
  StrategicGoal = 'strategic_goal',
}

type FloraContextObject = {
  type: FloraSupportedObjectTypes;
  id: string;
  name: string;
};

type FloraContextualInfo = {
  currentModule: GeneralViewNames;
  currentObjects: FloraContextObject[];
  startDate: string | null;
  endDate: string | null;
  selectedMeasures?: string[];
};

const FLORA_ASIDE_MIN_WIDTH = 325;
const FLORA_ASIDE_RIGHT_GAP = 10;
const FLORA_ASIDE_MAX_WIDTH_RATIO = 0.4;

export {
  AutoModeOutcome,
  FLORA_ASIDE_MAX_WIDTH_RATIO,
  FLORA_ASIDE_MIN_WIDTH,
  FLORA_ASIDE_RIGHT_GAP,
  FloraNewChatReason,
  FloraSupportedObjectTypes,
  FloraVersion,
};
export type {
  ArtifactSpec,
  ArtifactType,
  AskUserQuestion,
  ChatHistory,
  ContentBlock,
  Document,
  FloraContextObject,
  FloraContextualInfo,
  Message,
  MessageAttachment,
  MessageSegment,
  SubagentInfo,
  TokenUsage,
  ToolApproval,
  ToolApprovalDecision,
  ToolApprovalResolution,
  ToolCall,
  ToolCallStatus,
};
